import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import ABCDInput from "../../../../components/inputs/ABCDInput";

function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const LETTERS = ["A", "B", "C", "D"];

/**
 * Typ: zadanie maturalne zamknięte z wartościami bezwzględnymi
 * i pierwiastkiem: |√n - a| ± |√n - b|.
 *
 * W wariantach losowych √n leży między dwiema dodatnimi liczbami całkowitymi.
 * Dzięki temu dokładnie jeden moduł wymaga zmiany znaku. Losujemy kolejność
 * modułów oraz znak działania między nimi.
 */
/**
 * Wariant „oryginał” – dokładne dane z arkusza CKE.
 * Matura podstawowa, sesja sierpień 2025, zadanie 1:
 *   |√5 − 3| + |√5 − 1|  →  C. 2
 * Dystraktory są takie jak w arkuszu (różnią się od losowych).
 */
function createOriginalTask() {
  const n = 5;
  const low = 1;
  const high = 3;
  const value = high - low; // 2
  const options = [
    { label: "A", latex: "2\\sqrt{5} - 4", isCorrect: false },
    { label: "B", latex: "2\\sqrt{5} + 4", isCorrect: false },
    { label: "C", latex: "2", isCorrect: true },
    { label: "D", latex: "4", isCorrect: false },
  ];
  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaAbsValueClosedTask",
    data: {
      n,
      low,
      high,
      value,
      region: "between",
      exprSign: "+",
      options,
      correctIndex,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 1,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  // W każdym wariancie dokładnie jeden moduł wymaga zmiany znaku.
  const variants = [
    { id: "between_plus", region: "between", exprSign: "+", correctKey: "DIFF" },
    { id: "between_minus", region: "between", exprSign: "-", correctKey: "LEFT" },
  ];
  const courseVariants = variants;
  const forcedCourseVariant = Number.isInteger(generationOptions.courseSlot)
    ? courseVariants[generationOptions.courseSlot]
    : null;
  const variant = forcedCourseVariant || variants[getRandomInt(0, variants.length - 1)];
  const reverseOperands = Math.random() < 0.5;

  const low = getRandomInt(2, 5);
  const high = low + getRandomInt(1, 3);
  const minN = low * low + 1;
  const maxN = high * high - 1;
  let n;
  do { n = getRandomInt(minN, maxN); } while (Number.isInteger(Math.sqrt(n)));

  const value = high - low; // różnica b - a
  const sumLH = low + high;
  const correctKey =
    variant.id === "between_minus"
      ? reverseOperands
        ? "RIGHT"
        : "LEFT"
      : variant.correctKey;

  // cztery możliwe postacie wyniku
  const allTemplates = [
    {
      key: "DIFF",
      latex: `${value}`,
    },
    {
      key: "RIGHT",
      latex: `2\\sqrt{${n}} - ${sumLH}`,
    },
    {
      key: "LEFT",
      latex: `${sumLH} - 2\\sqrt{${n}}`,
    },
    {
      key: "NEG_DIFF",
      latex: `${low - high}`,
    },
  ];

  const optionsWithCorrectFlag = allTemplates.map((tpl) => ({
    ...tpl,
    isCorrect: tpl.key === correctKey,
  }));

  const shuffled = shuffle(optionsWithCorrectFlag);

  const options = shuffled.map((opt, idx) => ({
    label: LETTERS[idx],
    latex: opt.latex,
    isCorrect: opt.isCorrect,
  }));

  const correctIndex = options.findIndex((o) => o.isCorrect);

  return {
    type: "MaturaAbsValueClosedTask",
    data: {
      n,
      low,
      high,
      value,
      region: variant.region,
      exprSign: variant.exprSign,
      reverseOperands,
      options,
      correctIndex,
    },
    source: { variant: "random", basedOn: "MATURA_2025_SIE_1" },
  };
}

export function renderTask({ data }) {
  const { n, low, high, options, exprSign, reverseOperands } = data;

  const signLatex = exprSign === "-" ? " - " : " + ";
  const first = reverseOperands ? low : high;
  const second = reverseOperands ? high : low;
  const exprLatex = `\\left|\\sqrt{${n}} - ${first}\\right|${signLatex}\\left|\\sqrt{${n}} - ${second}\\right|`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p>
        <strong>Zadanie maturalne – zamknięte.</strong>
      </p>
      <p>
        Liczba <InlineMath math={exprLatex} /> jest równa:
      </p>

      {/* W TaskRunnerze widać od razu odpowiedzi A–D */}
      <div style={{ marginTop: 8 }}>
        {options.map((opt, idx) => (
          <div key={idx} style={{ marginBottom: 4 }}>
            <InlineMath math={`${opt.label}.\\; ${opt.latex}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Input używany w QuizRunnerze:
 * - litery A–D są klikalnymi „bąbelkami”,
 * - same treści odpowiedzi są już wypisane wyżej przez renderTask.
 */
export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { options } = data;
  const choiceIndex =
    userAnswer && typeof userAnswer.choiceIndex === "number"
      ? userAnswer.choiceIndex
      : null;

  const handleChange = (idx) => {
    const opt = options[idx];
    onChange({
      choiceIndex: idx,
      label: opt.label,
      latex: opt.latex,
    });
  };

  const letterOptions = options.map((opt, idx) => ({
    value: idx,
    label: opt.label,
    textNode: opt.label,
  }));

  return (
    <ABCDInput
      options={letterOptions}
      value={choiceIndex}
      onChange={handleChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  if (typeof userAnswer.choiceIndex !== "number") return false;
  return userAnswer.choiceIndex === data.correctIndex;
}

export function renderAnswer({ data }) {
  const { options, correctIndex } = data;
  const correct = options[correctIndex];

  // bez napisu "Poprawna odpowiedź:"
  return <InlineMath math={`${correct.label}.\\; ${correct.latex}`} />;
}

export function renderExplanation({ data }) {
  const { n, low, high, value, region, exprSign, reverseOperands } = data;

  // --- KROK 1: porównanie sqrt(n) z low i high przez kwadraty (jak w open-tasku) ---
  const low2 = low * low;
  const high2 = high * high;

  let compareLatex = "";
  if (region === "between") {
    compareLatex = `${low} < \\sqrt{${n}} < ${high} \\quad\\Longleftrightarrow\\quad ${low2} < ${n} < ${high2}`;
  } else if (region === "right") {
    compareLatex = `${high} < \\sqrt{${n}} \\quad\\Longleftrightarrow\\quad ${high2} < ${n}`;
  } else {
    compareLatex = `\\sqrt{${n}} < ${low} \\quad\\Longleftrightarrow\\quad ${n} < ${low2}`;
  }

  // --- KROK 2: zdejmowanie wartości bezwzględnych ---
  let absHigh;
  let absLow;

  if (region === "between") {
    absHigh = `${high} - \\sqrt{${n}}`;
    absLow = `\\sqrt{${n}} - ${low}`;
  } else if (region === "right") {
    absHigh = `\\sqrt{${n}} - ${high}`;
    absLow = `\\sqrt{${n}} - ${low}`;
  } else {
    absHigh = `${high} - \\sqrt{${n}}`;
    absLow = `${low} - \\sqrt{${n}}`;
  }

  const stepAbs1 = `\\left|\\sqrt{${n}} - ${high}\\right| = ${absHigh}`;
  const stepAbs2 = `\\left|\\sqrt{${n}} - ${low}\\right| = ${absLow}`;

  // --- KROK 3: składamy wynik (plus/minus) ---
  const signLatex = exprSign === "-" ? " - " : " + ";
  const first = reverseOperands ? low : high;
  const second = reverseOperands ? high : low;
  const fullExpr = `\\left|\\sqrt{${n}} - ${first}\\right|${signLatex}\\left|\\sqrt{${n}} - ${second}\\right|`;

  let substitutedLatex = "";
  let intermediateLatex = "";
  let resultLatex = "";
  const firstAbs = reverseOperands ? absLow : absHigh;
  const secondAbs = reverseOperands ? absHigh : absLow;

  // Kontynuowana linia rachunków kończy się znakiem "=",
  // a kolejna linia zaczyna się od "=", zgodnie z zapisem maturalnym.
  if (region === "between" && exprSign === "+") {
    substitutedLatex = `(${firstAbs}) + (${secondAbs}) =`;
    resultLatex = `= ${high} - ${low} = ${value}`;
  } else if (region === "right" && exprSign === "+") {
    substitutedLatex = `(${firstAbs}) + (${secondAbs}) =`;
    resultLatex = `= 2\\sqrt{${n}} - ${high + low}`;
  } else if (region === "between" && exprSign === "-") {
    substitutedLatex = `(${firstAbs}) - (${secondAbs}) =`;
    if (reverseOperands) {
      intermediateLatex =
        `= \\sqrt{${n}} - ${low} - ${high} + \\sqrt{${n}} =`;
      resultLatex = `= 2\\sqrt{${n}} - ${high + low}`;
    } else {
      intermediateLatex =
        `= ${high} - \\sqrt{${n}} - \\sqrt{${n}} + ${low} =`;
      resultLatex = `= ${high + low} - 2\\sqrt{${n}}`;
    }
  } else if (region === "right" && exprSign === "-") {
    substitutedLatex = `(${firstAbs}) - (${secondAbs}) =`;
    resultLatex = reverseOperands
      ? `= ${high} - ${low}`
      : `= ${low} - ${high}`;
  } else {
    substitutedLatex = `(${firstAbs}) ${exprSign} (${secondAbs}) =`;
  }

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1.</strong> Ustalamy, gdzie leży{" "}
        <InlineMath math={`\\sqrt{${n}}`} /> względem{" "}
        <InlineMath math={`${low}`} /> i <InlineMath math={`${high}`} />.
        Najwygodniej porównać kwadraty:
        <div style={{ textAlign: "center" }}>
          <BlockMath math={compareLatex} />
        </div>
      </div>

      <div>
        <strong>Krok 2.</strong> Zdejmujemy wartości bezwzględne korzystając z reguły:{" "}
        <span style={{ marginLeft: 8 }}>
          <InlineMath
            math={`|A| = A\\ \\text{gdy}\\ A\\ge 0,\\ \\ a\\ \\ |A| = -A\\ \\text{gdy}\\ A<0`}
          />
        </span>
        <div style={{ textAlign: "center" }}>
          <BlockMath math={stepAbs1} />
          <BlockMath math={stepAbs2} />
        </div>
      </div>

      <div>
        <strong>Krok 3.</strong> Podstawiamy do wyrażenia{" "}
        <InlineMath math={fullExpr} /> i{" "}
        {exprSign === "+" ? "dodajemy" : "odejmujemy"}:
        <div style={{ textAlign: "center" }}>
          <BlockMath math={substitutedLatex} />
          {intermediateLatex ? <BlockMath math={intermediateLatex} /> : null}
          {resultLatex ? <BlockMath math={resultLatex} /> : null}
        </div>
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer !== "object" ||
    typeof userAnswer.choiceIndex !== "number"
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const { label, latex } = userAnswer;
  if (!label || !latex) {
    return <i>(brak odpowiedzi)</i>;
  }

  return (
    <div style={{ marginTop: "1rem" }}>
      <span>Twoja odpowiedź: </span>
      <InlineMath math={`${label}.\\; ${latex}`} />
    </div>
  );
}
